import { useState, useEffect } from 'react';
import { Search, Bell, TrendingUp, Send, ExternalLink } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { SalesIntentSignal, IntentLevel } from '../../types/agent';
import { MetricCard, Badge, EmptyState } from '../shared';

export function SalesIntent() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState<SalesIntentSignal[]>([]);
  const [filterLevel, setFilterLevel] = useState<IntentLevel | 'all'>('all');

  useEffect(() => {
    loadSignals();
  }, [user]);

  const loadSignals = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'salesIntentSignals'),
        where('userId', '==', user.uid),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      setSignals(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as SalesIntentSignal));
    } catch (error) {
      console.error('Error loading sales intent signals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoute = async (signalId: string, destination: string) => {
    try {
      await updateDoc(doc(db, 'salesIntentSignals', signalId), {
        routed: true,
        routedTo: destination
      });
      await loadSignals();
    } catch (error) {
      console.error('Error routing signal:', error);
      alert('Failed to route signal');
    }
  };

  const highIntentSignals = signals.filter(s => s.intentLevel === 'high');
  const mediumIntentSignals = signals.filter(s => s.intentLevel === 'medium');
  const lowIntentSignals = signals.filter(s => s.intentLevel === 'low');
  const unroutedHighIntent = highIntentSignals.filter(s => !s.routed);

  const filteredSignals =
    filterLevel === 'all' ? signals : signals.filter(s => s.intentLevel === filterLevel);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Sales Intent</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search signals..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
          <h1 className="text-3xl font-semibold text-gray-900">Sales Intent Signals</h1>
          <p className="text-sm text-gray-600 mt-1">Identify and route high-value sales opportunities from AI interactions</p>
        </div>

        {/* 3-Stat Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <MetricCard
            label="High Intent"
            value={highIntentSignals.length.toString()}
            
          />
          <MetricCard
            label="Medium Intent"
            value={mediumIntentSignals.length.toString()}
            
          />
          <MetricCard
            label="Low Intent"
            value={lowIntentSignals.length.toString()}
            
          />
        </div>

        {/* Intent Level Filters */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-gray-700 font-medium">Filter:</span>
          {(['all', 'high', 'medium', 'low'] as const).map(level => (
            <button
              key={level}
              onClick={() => setFilterLevel(level)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterLevel === level
                  ? 'bg-t3-primary text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {level === 'all' ? 'All' : `${level.charAt(0).toUpperCase() + level.slice(1)} Intent`}
            </button>
          ))}
        </div>

        {/* High Intent Action Panel */}
        {unroutedHighIntent.length > 0 && (
          <div className="bg-gradient-to-r from-red-50 to-purple-50 border-2 border-critical-red rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-2">
                  🚨 {unroutedHighIntent.length} High Intent Signal{unroutedHighIntent.length !== 1 ? 's' : ''} Requiring Action
                </h2>
                <p className="text-sm text-gray-700 mb-4">
                  These prospects are showing strong buying signals. Route them to your sales team immediately.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => alert('Bulk route functionality coming soon')}
                    className="px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                  >
                    Route All to Sales →
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white text-sm font-medium">
                    Export to CRM
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-critical-red">{unroutedHighIntent.length}</div>
                <div className="text-sm text-gray-600">unrouted</div>
              </div>
            </div>
          </div>
        )}

        {/* Signals List */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              {filterLevel === 'all' ? 'All Intent Signals' : `${filterLevel.charAt(0).toUpperCase() + filterLevel.slice(1)} Intent Signals`}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredSignals.length} {filteredSignals.length === 1 ? 'signal' : 'signals'}
            </p>
          </div>

          {filteredSignals.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={TrendingUp}
                title="No intent signals"
                description="Sales intent signals will appear here as your agent detects buying signals"
              />
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredSignals.map(signal => (
                <div
                  key={signal.id}
                  className={`p-6 ${
                    signal.intentLevel === 'high'
                      ? 'bg-red-50 border-l-4 border-l-critical-red'
                      : signal.intentLevel === 'medium'
                      ? 'bg-yellow-50 border-l-4 border-l-warning-amber'
                      : 'bg-blue-50 border-l-4 border-l-blue-400'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge
                          variant={
                            signal.intentLevel === 'high'
                              ? 'critical'
                              : signal.intentLevel === 'medium'
                              ? 'warning'
                              : 'primary'
                          }
                        >
                          {signal.intentLevel} intent
                        </Badge>
                        {signal.routed && (
                          <Badge variant="success">
                            Routed to {signal.routedTo}
                          </Badge>
                        )}
                        <span className="text-xs text-gray-600">
                          {signal.timestamp.toDate().toLocaleString()}
                        </span>
                      </div>

                      <p className="text-sm text-gray-900 font-medium mb-2">{signal.query}</p>

                      {/* Intent Signals */}
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        {signal.signals.map((s, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-white rounded border border-gray-200 text-xs text-gray-700"
                          >
                            {s}
                          </span>
                        ))}
                      </div>

                      {/* Contact Info */}
                      {signal.contactInfo && (
                        <div className="flex items-center gap-4 text-xs text-gray-700 mb-2">
                          {signal.contactInfo.email && (
                            <span>📧 {signal.contactInfo.email}</span>
                          )}
                          {signal.contactInfo.phone && (
                            <span>📱 {signal.contactInfo.phone}</span>
                          )}
                          {signal.contactInfo.company && (
                            <span>🏢 {signal.contactInfo.company}</span>
                          )}
                        </div>
                      )}

                      {/* Response Preview */}
                      <details className="text-sm text-gray-600 mt-2">
                        <summary className="cursor-pointer hover:text-gray-900">
                          View full conversation
                        </summary>
                        <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-1">Response</p>
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{signal.response}</p>
                        </div>
                      </details>

                      {signal.notes && (
                        <div className="mt-2 p-2 bg-blue-100 rounded text-xs text-gray-700">
                          <strong>Notes:</strong> {signal.notes}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {!signal.routed && (
                      <div className="ml-4 flex flex-col gap-2">
                        <button
                          onClick={() => handleRoute(signal.id, 'Sales Team')}
                          className="px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700 text-sm font-medium whitespace-nowrap flex items-center gap-2"
                        >
                          <Send className="w-4 h-4" />
                          Route to Sales
                        </button>
                        <button
                          onClick={() => handleRoute(signal.id, 'CRM')}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white text-sm font-medium whitespace-nowrap flex items-center gap-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Send to CRM
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">About Sales Intent Detection</h3>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>• High intent: Direct buying signals (pricing, demo requests, comparisons)</li>
            <li>• Medium intent: Research phase (feature questions, use case exploration)</li>
            <li>• Low intent: General interest (informational queries, browsing)</li>
            <li>• Integrate with your CRM to auto-route high intent signals to your sales team</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
